// Wayne AI Platform — Memory stream Zustand slice.
//
// Accumulates real-time `memory.extracted` and `memory.promoted` events from
// the WebSocket, providing the data model for the Memory sidebar/panel.
//
// State:
//   - `items[]`        — extracted memory items awaiting user action
//   - `promotedIds[]`  — IDs of memories that the system auto-promoted
//   - `lastSeq`        — highest seq processed (for gap-detect on reconnect)
//
// Items older than 5 minutes are considered stale and are auto-dismissed when
// the store processes a new event. This keeps the sidebar from accumulating
// unbounded entries during long sessions.

import { create } from 'zustand';
import type { ServerEvent } from '@/services/ws/types';

// -- Constants ----------------------------------------------------------------

/** Items older than this are automatically removed on next event application. */
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

// -- Types --------------------------------------------------------------------

export interface ExtractedMemoryItem {
  readonly memoryId: string;
  readonly category: string;
  readonly content: string;
  readonly confidence: number;
  readonly autoPromote: boolean;
  readonly timestamp: number;
  readonly saved: boolean;
}

export interface MemoryStreamState {
  items: readonly ExtractedMemoryItem[];
  promotedIds: readonly string[];
  lastSeq: number;

  /** Apply a single WebSocket event to the store. Pure reducer (no async). */
  applyEvent: (event: MemoryStreamEvent | ServerEvent) => void;
  /** Mark an item as saved (for UI feedback after user confirms). */
  markSaved: (memoryId: string) => void;
  /** Remove an item from the list (user dismisses it). */
  dismissItem: (memoryId: string) => void;
  /** Wipe state on logout / session-switch. */
  reset: () => void;
}

// -- Event shapes -------------------------------------------------------------

export interface MemoryExtractedEvent {
  type: 'memory.extracted';
  seq: number;
  ts: number;
  sessionId: string;
  payload: {
    memoryId: string;
    category: string;
    content: string;
    confidence: number;
    autoPromote: boolean;
  };
}

export interface MemoryPromotedEvent {
  type: 'memory.promoted';
  seq: number;
  ts: number;
  sessionId: string;
  payload: {
    memoryId: string;
  };
}

export type MemoryStreamEvent = MemoryExtractedEvent | MemoryPromotedEvent;

// -- Helpers ------------------------------------------------------------------

/**
 * Filter out items whose timestamp is older than `STALE_THRESHOLD_MS` relative
 * to `now`. Called on every event application to keep the list bounded.
 */
function pruneStaleItems(
  items: readonly ExtractedMemoryItem[],
  now: number,
): readonly ExtractedMemoryItem[] {
  return items.filter((item) => now - item.timestamp < STALE_THRESHOLD_MS);
}

// -- Initial state ------------------------------------------------------------

const initialState = (): Pick<MemoryStreamState, 'items' | 'promotedIds' | 'lastSeq'> => ({
  items: [],
  promotedIds: [],
  lastSeq: 0,
});

// -- Reducer ------------------------------------------------------------------

/**
 * Pure reducer over a memory stream event. Exported separately from the store
 * so unit tests can verify the transitions without instantiating Zustand.
 */
export function reduceMemoryEvent(
  state: Pick<MemoryStreamState, 'items' | 'promotedIds' | 'lastSeq'>,
  event: MemoryStreamEvent | ServerEvent,
): Pick<MemoryStreamState, 'items' | 'promotedIds' | 'lastSeq'> {
  // Out-of-order / duplicate guard.
  if (event.seq <= state.lastSeq) {
    return state;
  }

  const lastSeq = event.seq;
  const now = Date.now();

  switch (event.type) {
    case 'memory.extracted': {
      // Prune stale items first.
      const fresh = pruneStaleItems(state.items, now);

      // Normalise payload: ServerEvent wraps in `memories[]`, legacy has flat fields.
      const memories: Array<{
        memoryId: string;
        category: string;
        content: string;
        confidence: number;
        autoPromote: boolean;
      }> = 'memories' in event.payload
        ? event.payload.memories
        : [event.payload];

      // Deduplicate and build new items.
      const existingIds = new Set(fresh.map((i) => i.memoryId));
      const newItems: ExtractedMemoryItem[] = memories
        .filter((m) => !existingIds.has(m.memoryId))
        .map((m) => ({
          memoryId: m.memoryId,
          category: m.category,
          content: m.content,
          confidence: m.confidence,
          autoPromote: m.autoPromote,
          timestamp: event.ts,
          saved: false,
        }));

      if (newItems.length === 0) {
        return { ...state, items: fresh, lastSeq };
      }

      return {
        ...state,
        items: [...fresh, ...newItems],
        lastSeq,
      };
    }

    case 'memory.promoted': {
      // Prune stale items.
      const fresh = pruneStaleItems(state.items, now);

      // Deduplicate promotedIds.
      const alreadyPromoted = state.promotedIds.includes(event.payload.memoryId);
      const promotedIds = alreadyPromoted
        ? state.promotedIds
        : [...state.promotedIds, event.payload.memoryId];

      return {
        ...state,
        items: fresh,
        promotedIds,
        lastSeq,
      };
    }

    default:
      // Non-memory events should not reach this reducer (WsBridgeProvider
      // guards), but if they do, return state unchanged.
      return state;
  }
}

// -- Store --------------------------------------------------------------------

export const useMemoryStreamStore = create<MemoryStreamState>((set) => ({
  ...initialState(),

  applyEvent: (event) => set((state) => reduceMemoryEvent(state, event)),

  markSaved: (memoryId) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.memoryId === memoryId ? { ...item, saved: true } : item,
      ),
    })),

  dismissItem: (memoryId) =>
    set((state) => ({
      items: state.items.filter((item) => item.memoryId !== memoryId),
    })),

  reset: () => set(initialState()),
}));

/** Test helper. */
export function __resetMemoryStreamStoreForTests(): void {
  useMemoryStreamStore.setState(initialState());
}
